<template>
  <div :class="['landing-root', { 'dark-mode': isDarkMode }]" @copy.prevent @cut.prevent>
    <StockTicker :static-items="tickerItems" />
    <div class="top-bar-buttonz">
      <div class="launch-links">
        <button class="launch-links-toggle" type="button" @click="toggleLaunchLinks" aria-label="Show launch links">
          <md-icon class="far fa-user"></md-icon>
        </button>
        <div v-if="showLaunchLinks" class="launch-link-item">
          <a class="launch-link-btn" href="https://play.accountonomics.net" target="_blank" rel="noopener noreferrer">
            <span>Student</span>
          </a>
        </div>
        <div v-if="showLaunchLinks" class="launch-link-item">
          <a class="launch-link-btn" href="https://educator.accountonomics.net" target="_blank" rel="noopener noreferrer">
            <span>Educator</span>
          </a>
        </div>
      </div>
      <div v-if="isDarkMode" @click="toggleTheme" :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'" class="dark-mode-moon">
        <md-icon class="fas fa-moon" style="color: var(--moon-color);"></md-icon>
      </div>
      <div v-else @click="toggleTheme" :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'" class="dark-mode-moon">
        <md-icon class="far fa-moon" style="color: var(--moon-color);"></md-icon>
      </div>
    </div>
    <div class="container" style="margin-bottom: 64px;">
      <div class="content-1">
        <div class="acnt-logo-area">
          <h1 class="logo-text main">
            <span class="spin-dollar">a</span>ccountonomics
          </h1>
        </div>
        <p class="description">Accountonomics puts students in charge of a simulated company, and their decisions drive business results in a competitive market.</p>
        <div class="links">
          <a href="/Instructions.pdf" target="_blank" style="color: black;">
            <md-button class="button-link">View The Instructions (PDF)</md-button>
          </a>
          <a @click="scrollToInfo" >
            <md-button class="button-link secondary">More Info</md-button>
          </a>
        </div>
      </div>
    </div>
    <div class="container info-container">
      <div class="round-components-container step-desc">
        <div class="round-1-card-stack">
          <h4 style="position: absolute; left: 50%; top: -40px; transform: translateX(-50%); border: 2px solid var(--border-color); padding: 2px; border-radius: 3px;">OG Capital ($OGC)</h4>
          <div class="round-1-card stock-preview">
            <Stock :static-mode="true" :static-config="stockDemoConfig" />
          </div>
          <div class="round-1-card equipment-preview">
            <Equipment :static-mode="true" :static-round-config="equipmentDemoConfig" />
          </div>
          <div class="round-1-card product-preview">
            <Product :static-mode="true" :static-round-config="productDemoConfig" :static-dark-mode="isDarkMode" />
          </div>
        </div>
      </div>
      <div class="step-desc">
        <div class="step-copy">
          <h1 class="logo-text">
            Step 1
          </h1>
          <p class="description" style="text-align: left;">Students make capital and operating decisions for their business.</p>
        </div>
      </div>
    </div>
    <div class="container info-container">
      <div class="step-desc">
        <div class="step-copy">
          <h1 class="logo-text">
            Step 2
          </h1>
          <p class="description" style="text-align: left;">Students view (or, if enabled, report) their business's financial results each round.</p>
        </div>
      </div>
      <div class="round-components-container step-desc">
        <div class="financial-stage">
          <div class="financial-stage-panel">
            <IncomeStatement
              v-show="activeFinancialView === 'income'"
              :static-mode="true"
              :allow-submission-when-valid-game-config-and-game-state="true"
              :static-config="incomeStatementDemoConfig"
            />
            <BalanceSheet
              v-show="activeFinancialView === 'balance'"
              :static-mode="true"
              :allow-submission-when-valid-game-config-and-game-state="true"
              :static-config="balanceSheetDemoConfig"
            />
            <CashFlow
              v-show="activeFinancialView === 'cash'"
              :static-mode="true"
              :allow-submission-when-valid-game-config-and-game-state="true"
              :static-config="cashFlowDemoConfig"
            />
          </div>
          <div class="financial-stage-nav">
            <button type="button" class="financial-stage-nav-btn" :class="{ active: activeFinancialView === 'income' }" @click="setFinancialView('income')">Income Statement</button>
            <button type="button" class="financial-stage-nav-btn" :class="{ active: activeFinancialView === 'balance' }" @click="setFinancialView('balance')">Balance Sheet</button>
            <button type="button" class="financial-stage-nav-btn" :class="{ active: activeFinancialView === 'cash' }" @click="setFinancialView('cash')">Cash Flow</button>
          </div>
        </div>
      </div>
    </div>
    <div class="container info-container" style="margin-bottom: 32px;">
      <div class="round-components-container step-desc">
        <div class="scoreboard-stage">
          <div class="scoreboard-stage-panel">
            <div v-show="activeScoreboardView === 'net-income'" class="scoreboard-stage-view">
              <div class="scoreboard-stage-card">
                <h2 class="scoreboard-stage-title" style="text-align: center;">Round 4 Net Income</h2>
                <bar-graph :data="step3RoundByRoundData" :options="step3RoundByRoundOptions"></bar-graph>
              </div>
            </div>
            <div v-show="activeScoreboardView === 'financial-ratios'" class="scoreboard-stage-view">
              <div class="scoreboard-stage-card">
                <h2 class="scoreboard-stage-title" style="text-align: center;">Financial Ratios</h2>
                <financial-ratios-table
                  class="scoreboard-ratios-preview"
                  :static-mode="true"
                  :static-stock-configured="true"
                  :static-rows="step3FinancialRatiosRows"
                ></financial-ratios-table>
              </div>
            </div>
            <div v-show="activeScoreboardView === 'products'" class="scoreboard-stage-view">
              <div class="scoreboard-stage-card scoreboard-products-card">
                <div class="product-graph-row">
                  <h2 class="scoreboard-stage-title" style="text-align: center;">Product 1</h2>
                  <span style="font-size: 9px;">*Quarterly Regrets $QRGT did not sell any units this round.</span>
                  <div class="md-layout">
                    <div class="md-layout-item">
                      <h3>Sales Quantity (units)</h3>
                      <pie-chart :data="step3ProductChartDataUnitSales" :options="step3ProductChartOptionsUnitSales"></pie-chart>
                    </div>
                    <div class="md-layout-item">
                      <h3>Sales Amount ($)</h3>
                      <pie-chart :data="step3ProductChartDataDollarSales" :options="step3ProductChartOptionsDollarSales"></pie-chart>
                    </div>
                  </div>
                  <!-- <team-data-table :team-data="step3ProductTeamData"></team-data-table> -->
                </div>
              </div>
            </div>
          </div>
          <div class="financial-stage-nav">
            <button type="button" class="financial-stage-nav-btn" :class="{ active: activeScoreboardView === 'net-income' }" @click="setScoreboardView('net-income')">Net Income</button>
            <button type="button" class="financial-stage-nav-btn" :class="{ active: activeScoreboardView === 'financial-ratios' }" @click="setScoreboardView('financial-ratios')">Financial Ratios</button>
            <button type="button" class="financial-stage-nav-btn" :class="{ active: activeScoreboardView === 'products' }" @click="setScoreboardView('products')">Products</button>
          </div>
        </div>
      </div>
      <div class="step-desc">
        <div class="step-copy">
          <h1 class="logo-text">
            Step 3
          </h1>
          <p class="description" style="text-align: left;">Students see their team’s results ranked against other classmates on profitability, market domination, and overall company performance.</p>
        </div>
      </div>
    </div>
    <div class="container info-container" id="gallery-btn" >
      <a href="/gallery"><button class="button-link" >SEE THE GALLERY</button></a>
    </div>
    <footer>
      <div class="footer">
          <h4 class="footer-text">
            <span class="">accountonomics</span>
          </h4>
          <h4 class="footer-text">
            <span>© 2026 Accountonomics, All rights reserved.</span>
          </h4>
          <h4 class="footer-text">
            <span class="footer-email-selectable">support@accountonomics.net</span>
          </h4>
      </div>
    </footer>
  </div>
</template>

<script>
import Equipment from '../components/Equipment.vue';
import BarGraph from '../components/BarGraph.vue';
import BalanceSheet from '../components/BalanceSheet.vue';
import CashFlow from '../components/CashFlow.vue';
import FinancialRatiosTable from '../components/FinancialRatiosTable.vue';
import IncomeStatement from '../components/IncomeStatement.vue';
import PieChart from '../components/PieChart.vue';
import Product from '../components/Product.vue';
import Stock from '../components/Stock.vue';
import StockTicker from '../components/StockTicker.vue';

export default {
  name: 'LandingHome',
  components: {
    Equipment,
    BarGraph,
    BalanceSheet,
    CashFlow,
    FinancialRatiosTable,
    IncomeStatement,
    PieChart,
    Product,
    Stock,
    StockTicker
    // TeamDataTable,
  },
  data() {
    return {
      isDarkMode: true,
      showLaunchLinks: false,
      activeFinancialView: 'income',
      activeScoreboardView: 'net-income',
      stockDemoConfig: {
        beginningOfRoundStockPrice: 121.32,
        totalSellableSharesPerTeam: 500000,
        initialOfferingNumShares: 100000,
        numSharesSold: 180000,
        previouslySoldStocks: 180000,
        defaultNumStocksOffered: 165
      },
      equipmentDemoConfig: {
        equipments: [
          {
            id: 'landing-equipment',
            typesConfig: [
              {
                miniId: '204',
                equipmentType: 'New',
                purchaseCost: 45000,
                usefulLife: 8,
                productionCapacity: 28000,
                depreciationMethods: ['Straight Line', 'Double Declining'],
                defaultDepreciationMethod: 'Straight Line'
              }
            ]
          }
        ]
      },
      productDemoConfig: {
        products: [
          {
            id: 'landing-product',
            gameProductId: 'landing-product-1',
            name: 'Product 1',
            productionCost: 22,
            demandSlope: -4,
            demandIntercept: 54
          }
        ]
      },
      incomeStatementDemoConfig: {
        statement: {
          revenue: 520000,
          returns: -10000,
          net: 510000,
          costOfGoodsSold: -280000,
          grossProfit: 230000,
          adExpense: -20000,
          depreciationExpense: -18000,
          interest: -6000,
          gainOnAssetSales: 5000,
          total: 191000,
          incomeTax: -47750,
          netIncome: 143250
        },
        correctAnswers: [
          { fieldName: 'revenue', correctAnswer: 540000 },
          { fieldName: 'returns', correctAnswer: -12000 },
          { fieldName: 'net', correctAnswer: 528000 },
          { fieldName: 'costOfGoodsSold', correctAnswer: -286000 },
          { fieldName: 'grossProfit', correctAnswer: 242000 },
          { fieldName: 'adExpense', correctAnswer: -22000 },
          { fieldName: 'depreciationExpense', correctAnswer: -18000 },
          { fieldName: 'interest', correctAnswer: -6000 },
          { fieldName: 'gainOnAssetSales', correctAnswer: 5000 },
          { fieldName: 'total', correctAnswer: 201000 },
          { fieldName: 'incomeTax', correctAnswer: -50250 },
          { fieldName: 'netIncome', correctAnswer: 150750 }
        ],
        incorrectAnswers: [
          { fieldName: 'revenue', correctAnswer: 540000 },
          { fieldName: 'returns', correctAnswer: -12000 },
          { fieldName: 'net', correctAnswer: 528000 },
          { fieldName: 'costOfGoodsSold', correctAnswer: -286000 },
          { fieldName: 'grossProfit', correctAnswer: 242000 },
          { fieldName: 'adExpense', correctAnswer: -22000 },
          { fieldName: 'total', correctAnswer: 201000 },
          { fieldName: 'incomeTax', correctAnswer: -50250 },
          { fieldName: 'netIncome', correctAnswer: 150750 }
        ]
      },
      balanceSheetDemoConfig: {
        statement: {
          cash: 115000,
          accountsReceivable: 54000,
          inventory: 92000,
          equipment: 240000,
          equipmentDepreciation: -22000,
          furnishings: 32000,
          furnishingsDepreciation: -6000,
          buildings: 180000,
          buildingsDepreciation: -15000,
          land: 90000,
          totalAssets: 760000,
          loanPayable: 210000,
          totalLiabilities: 210000,
          commonStock: 350000,
          retainedEarnings: 200000,
          totalLiabilitiesAndEquity: 760000
        },
        correctAnswers: [
          { fieldName: 'cash', correctAnswer: 118000 },
          { fieldName: 'accountsReceivable', correctAnswer: 54000 },
          { fieldName: 'inventory', correctAnswer: 92000 },
          { fieldName: 'equipment', correctAnswer: 240000 },
          { fieldName: 'equipmentDepreciation', correctAnswer: -22000 },
          { fieldName: 'furnishings', correctAnswer: 32000 },
          { fieldName: 'furnishingsDepreciation', correctAnswer: -6000 },
          { fieldName: 'buildings', correctAnswer: 180000 },
          { fieldName: 'buildingsDepreciation', correctAnswer: -15000 },
          { fieldName: 'land', correctAnswer: 90000 },
          { fieldName: 'totalAssets', correctAnswer: 763000 },
          { fieldName: 'loanPayable', correctAnswer: 210000 },
          { fieldName: 'totalLiabilities', correctAnswer: 210000 },
          { fieldName: 'commonStock', correctAnswer: 350000 },
          { fieldName: 'retainedEarnings', correctAnswer: 203000 },
          { fieldName: 'totalLiabilitiesAndEquity', correctAnswer: 763000 }
        ],
        incorrectAnswers: [
          { fieldName: 'cash', correctAnswer: 118000 },
          { fieldName: 'totalAssets', correctAnswer: 763000 },
          { fieldName: 'retainedEarnings', correctAnswer: 203000 },
          { fieldName: 'totalLiabilitiesAndEquity', correctAnswer: 763000 }
        ],
        showTeamAnswers: true,
        showCorrectAnswers: true
      },
      cashFlowDemoConfig: {
        statement: {
          operatingActivitiesSales: 528000,
          operatingActivitiesPurchasedInventory: -286000,
          operatingActivitiesPaidAdvertising: -20000,
          operatingActivitiesSalesReturns: -12000,
          operatingActivitiesIncomeTaxesPaid: -50250,
          operatingActivitiesInterestPaid: -6000,
          operatingActivitiesTotal: 153750,
          investingActivitiesInflowEquipment: 12000,
          investingActivitiesInflowFurnishings: 2000,
          investingActivitiesInflowBuildings: 0,
          investingActivitiesInflowLand: 0,
          investingActivitiesOutflowEquipment: -45000,
          investingActivitiesOutflowFurnishings: -8000,
          investingActivitiesOutflowBuildings: 0,
          investingActivitiesOutflowLand: 0,
          investingActivitiesTotal: -39000,
          financingActivitiesCapital: 20000,
          financingActivitiesLoanBorrow: 30000,
          financingActivitiesLoanRepay: -10000,
          financingActivitiesTotal: 40000,
          netCash: 154750,
          beginningOfRound: 34000,
          endOfRound: 188750
        },
        correctAnswers: [
          { fieldName: 'operatingActivitiesSales', correctAnswer: 528000 },
          { fieldName: 'operatingActivitiesPurchasedInventory', correctAnswer: -286000 },
          { fieldName: 'operatingActivitiesPaidAdvertising', correctAnswer: -22000 },
          { fieldName: 'operatingActivitiesSalesReturns', correctAnswer: -12000 },
          { fieldName: 'operatingActivitiesIncomeTaxesPaid', correctAnswer: -50250 },
          { fieldName: 'operatingActivitiesInterestPaid', correctAnswer: -6000 },
          { fieldName: 'operatingActivitiesTotal', correctAnswer: 151750 },
          { fieldName: 'investingActivitiesInflowEquipment', correctAnswer: 12000 },
          { fieldName: 'investingActivitiesInflowFurnishings', correctAnswer: 2000 },
          { fieldName: 'investingActivitiesOutflowEquipment', correctAnswer: -45000 },
          { fieldName: 'investingActivitiesOutflowFurnishings', correctAnswer: -8000 },
          { fieldName: 'investingActivitiesTotal', correctAnswer: -39000 },
          { fieldName: 'financingActivitiesCapital', correctAnswer: 20000 },
          { fieldName: 'financingActivitiesLoanBorrow', correctAnswer: 30000 },
          { fieldName: 'financingActivitiesLoanRepay', correctAnswer: -10000 },
          { fieldName: 'financingActivitiesTotal', correctAnswer: 40000 },
          { fieldName: 'netCash', correctAnswer: 152750 },
          { fieldName: 'beginningOfRound', correctAnswer: 34000 },
          { fieldName: 'endOfRound', correctAnswer: 186750 }
        ],
        incorrectAnswers: [
          { fieldName: 'operatingActivitiesPaidAdvertising', correctAnswer: -22000 },
          { fieldName: 'operatingActivitiesTotal', correctAnswer: 151750 },
          { fieldName: 'netCash', correctAnswer: 152750 },
          { fieldName: 'endOfRound', correctAnswer: 186750 }
        ],
        showTeamAnswers: true,
        showCorrectAnswers: true
      },
      step3RoundByRoundOptions: {
        title: 'Mouseover for More Details',
        legend: 'none',
        height: 360,
        backgroundColor: 'transparent',
        hAxis: {
          format: 'currency',
          title: 'Net Income',
          minValue: 0,
        },
      },
      step3RoundByRoundData: [
        ['Team', 'Net Income', { type: 'string', role: 'tooltip' }, { role: 'style' }, { role: 'annotation' }],
        ['OG Capital', 152750, 'OGC $152,750.00', '#43b0f9', 'First Place'],
        ['Robux Ventures', 131400, 'RBUX $131,400.00', '#5fe0b3', 'Second Place'],
        ['Bagholder Society', 118900, 'HODL $118,900.00', '#f5c26b', 'Third Place'],
        ['Quarterly Regrets Inc.', -12750, 'QRGT $-12,750.00', '#e06c75', ''],
      ],
      step3FinancialRatiosRows: [
        {
          team: { name: 'OGC' },
          earningsPerShare: 5.84,
          priceEarnings: 20.78,
          debtToEquity: 0.61,
          returnOnEquity: 0.22,
          returnOnAssets: 0.13
        },
        {
          team: { name: 'RBUX' },
          earningsPerShare: 5.12,
          priceEarnings: 18.42,
          debtToEquity: 0.67,
          returnOnEquity: 0.19,
          returnOnAssets: 0.12
        },
        {
          team: { name: 'HODL' },
          earningsPerShare: 4.76,
          priceEarnings: 17.9,
          debtToEquity: 0.73,
          returnOnEquity: 0.17,
          returnOnAssets: 0.1
        },
        {
          team: { name: 'QRGT' },
          earningsPerShare: 2.88,
          priceEarnings: 13.66,
          debtToEquity: 0.88,
          returnOnEquity: 0.10,
          returnOnAssets: 0.08
        }
      ],
      step3ProductChartDataUnitSales: [
        ['Team Name', 'Total Units Sold', { type: 'string', role: 'tooltip' }],
        ['OG Capital', 4200, 'OGC 4,200 Units'],
        ['Robux Ventures', 3550, 'RBUX 3,550 Units'],
        ['Bagholder Society', 2980, 'HODL 2,980 Units']
        // ['Quarterly Regrets Inc.', 0, 'QRGT 0 Units']
      ],
      step3ProductChartDataDollarSales: [
        ['Team Name', 'Total Dollars Sold', { type: 'string', role: 'tooltip' }],
        ['OG Capital', 159600, 'OGC $159,600.00'],
        ['Robux Ventures', 134900, 'RBUX $134,900.00'],
        ['Bagholder Society', 113240, 'HODL $113,240.00'],
        ['Quarterly Regrets Inc.', 0, 'QRGT $0.00']
      ],
      step3ProductChartOptionsUnitSales: {
        legend: 'labeled',
        pieSliceText: 'none',
        backgroundColor: 'transparent',
        slices: {
          0: { color: '#43b0f9' },
          1: { color: '#5fe0b3' },
          2: { color: '#f5c26b' },
          3: { color: '#e06c75' }
        }
      },
      step3ProductChartOptionsDollarSales: {
        legend: 'labeled',
        pieSliceText: 'none',
        backgroundColor: 'transparent',
        slices: {
          0: { color: '#43b0f9' },
          1: { color: '#5fe0b3' },
          2: { color: '#f5c26b' },
          3: { color: '#e06c75' }
        }
      },
      step3ProductTeamData: [
        {
          rank: 1,
          teamName: 'OG Capital',
          offerPrice: 38,
          unitsOffered: 4600,
          unitsAdvertised: 4600,
          unitsSold: 4200,
          sales: 159600
        },
        {
          rank: 2,
          teamName: 'Robux Ventures',
          offerPrice: 38,
          unitsOffered: 3900,
          unitsAdvertised: 3900,
          unitsSold: 3550,
          sales: 134900
        },
        {
          rank: 3,
          teamName: 'Bagholder Society',
          offerPrice: 38,
          unitsOffered: 3250,
          unitsAdvertised: 3250,
          unitsSold: 2980,
          sales: 113240
        },
        {
          rank: 4,
          teamName: 'Quarterly Regrets Inc.',
          offerPrice: 37,
          unitsOffered: 2650,
          unitsAdvertised: 2650,
          unitsSold: 2410,
          sales: 89170
        },
        {
          rank: '',
          teamName: 'Total',
          offerPrice: '',
          unitsOffered: 14400,
          unitsAdvertised: 14400,
          unitsSold: 13140,
          sales: 496910
        }
      ],
      tickerItems: [
        {
          stockPrice: 121.32,
          changeFromPreviousRound: 1.42,
          team: { stockSymbol: 'RBUX' }
        },
        {
          stockPrice: 98.75,
          changeFromPreviousRound: -2.21,
          team: { stockSymbol: 'QRGT' }
        },
        {
          stockPrice: 143.08,
          changeFromPreviousRound: 0.88,
          team: { stockSymbol: 'OGC' }
        },
        {
          stockPrice: 111.67,
          changeFromPreviousRound: 0.63,
          team: { stockSymbol: 'HODL' }
        }
      ]
    };
  },
  methods: {
    setFinancialView(view) {
      this.activeFinancialView = view;
    },
    setScoreboardView(view) {
      this.activeScoreboardView = view;
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
    },
    toggleLaunchLinks() {
      this.showLaunchLinks = !this.showLaunchLinks;
    },
    hydrateTheme() {
      const savedTheme = localStorage.getItem('theme');
      this.isDarkMode = savedTheme !== 'light';
    },
    scrollToInfo() {
      const el = document.querySelector('.info-container');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  },
  mounted() {
    this.hydrateTheme();
  }
}
</script>

<style scoped>
.landing-root {
  --team-menu-color: #ffffff;
  --round-menu-color: #eeeef7;
  --main-bg-color: #ffffff;
  --nav-bar-color: #000000;
  --table-title-bg-color: #000000;
  --table-cell-color: #f0f0ff;
  --table-header-non-input-color: #e4e4f5;
  --inner-card-color: #ffffff;
  --inner-card-secondary: #ffffff;
  --main-box-color: #ffffff;
  --inner-box-color: #ffffff;
  --box-shadow-color: #888888;
  --text-primary: #000000;
  --text-secondary: #ffffff;
  --text-secondary-minor: #a5a5a5;
  --text-top-nav-text: #ffffff;
  --top-nav-activity-bg: #42dd13;
  --bottom-nav-activity-bg: #ffffff;
  --btn-primary-color: #43b0f9;
  --btn-secondary-color: #ffb3b3;
  --btn-warning-color: #ff5252;
  --success-bg-color: #28a745;
  --team-chip-color: #000000;
  --team-selected-bg-color: #123f98;
  --onboard-message-color: #0000ff;
  --warning-text-color: #ff5252;
  --salmon-dark: #f7a690;
  --moon-color: #000000;
  --bold-text-highligher: #ffffff;
  --scoreboard-item-bg: #eeeef7;
  --faded-text: #a5a5a5c2;
  --border-color: #000000;
  --selected-je-bg: #cec8de;
  --light-purple: #a554df;

  margin: 0;
  min-height: 100vh;
  height: calc(100% - 65px );
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  font-family: 'IBM Plex Mono', monospace;
  background: var(--main-bg-color);
  color: var(--text-primary);
  position: relative;
  user-select: none;
}

.landing-root.dark-mode {
  --team-menu-color: #000000;
  --round-menu-color: #000000;
  --main-bg-color: #000000;
  --nav-bar-color: #000000;
  --table-title-bg-color: #000000;
  --table-header-non-input-color: #000000;
  --inner-card-color: #000000;
  --scoreboard-item-bg: #000000;
  --main-box-color: #0f1411;
  --inner-box-color: #3a5445;
  --inner-card-secondary: #0d1a14;
  --text-primary: #ffffff;
  --text-secondary: #181825;
  --text-secondary-minor: #e6f5ee;
  --text-top-nav-text: #e6f5ee;
  --faded-text: #a5a5a5c2;
  --table-cell-color: #9fe6b8;
  --border-color: #2c5043;
  --box-shadow-color: #ffffff;
  --btn-primary-color: #3ddc97;
  --btn-secondary-color: #a456dc;
  --btn-warning-color: #f5c26b;
  --success-bg-color: #28a745;
  --top-nav-activity-bg: #1f8f5f;
  --bottom-nav-activity-bg: #2fbf71;
  --team-chip-color: #5fe0b3;
  --team-selected-bg-color: #1f8f5f;
  --selected-je-bg: #0f2a1f;
  --onboard-message-color: #c7f5d9;
  --warning-text-color: #f38ba8;
  --moon-color: #2fbf71;
  --light-purple: #5fe0b3;
  --bold-text-highligher: #3ddc97;
  --salmon-dark: #e06c75;
}

.container {
  background: var(--main-bg-color);
  width: 100%;
  min-height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 1.4rem;
}

.container-first {
  background: var(--main-bg-color);
  width: 100%;
  min-height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 1.4rem;
}


.content-1 {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 60%;;
}

h1 {
  font-size: 2.5rem;
  letter-spacing: -1px;
}

.description {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text-secondary-minor);
}

.links {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.button-link {
  border: none;
  border-radius: 2px;
  background: var(--btn-primary-color);
  color: #000;
  padding: 0.7rem 1.15rem;
  font-weight: 700;
  cursor: pointer;
}

.button-link.secondary {
  background: var(--btn-secondary-color);
}

.theme-toggle {
  position: absolute;
  top: 76px;
  right: 20px;
  padding: 0.45rem 0.65rem;
  border-radius: 2px;
  border: none;
  cursor: pointer;
  background: var(--btn-primary-color);
  color: #000000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.top-bar-buttonz {
  position: absolute;
  top: 64px;
  left: 0px;
  height: 64px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
}

.dark-mode-moon {
  max-width: 32px;
  cursor: pointer;
}

.launch-links {
  display: flex;
  gap: 12px;
}

.launch-links-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  border: none;
  padding: 8px 12px;
  background: var(--main-bg-color);
  color: var(--text-primary);
  cursor: pointer;
}

.launch-links-toggle .md-icon {
  color: var(--text-primary);
}

.launch-link-item {
  display: flex;
}

.launch-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 2px;
  border: none;
  padding: 8px 12px;
  background: var(--main-bg-color);
  border: 2px solid var(--btn-primary-color);
  color: #000000;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  z-index: 2;
}

.launch-link-btn span {
  line-height: 1;
  color: var(--text-primary);
  text-decoration: none;
  cursor: pointer;
}

.launch-link-btn:hover,
.launch-link-btn:focus,
.launch-link-btn:active,
.launch-link-btn:hover span,
.launch-link-btn:focus span,
.launch-link-btn:active span {
  text-decoration: none;
  cursor: pointer;
}

.acnt-logo-area {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 50px;
  margin-left: 20px;
}

.logo-text {
  margin: 0;
  display: flex;
  align-items: center;
}

.logo-text.main {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -1px;
}

.step-copy {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.spin-dollar {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: inherit;
  line-height: 1;
  margin-right: 2px;
  transform-origin: center;
  animation: spinFull 1.75s ease-in-out infinite;
}

.spin-dollar::before {
  content: '';
  position: absolute;
  top: -1%;
  bottom: -1%;
  left: 49%;
  width: 2px;
  border-radius: 2px;
  background: currentColor;
  transform: translateX(-50%);
  opacity: 0.9;
  pointer-events: none;
}

.financial-stage {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin: 0;
  padding: 0;
}

.financial-stage-panel {
  display: flex;
  justify-content: center;
  overflow-x: auto;
}

.financial-stage-panel ::v-deep .income-statement-container,
.financial-stage-panel ::v-deep .balance-sheet-container,
.financial-stage-panel ::v-deep .cash-flow-container {
  min-width: 680px;
  max-height: 100vh;
}

.financial-stage-nav {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.financial-stage-nav-btn {
  border: 1px solid var(--border-color);
  background: var(--main-bg-color);
  color: var(--text-primary);
  font-weight: 700;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  cursor: pointer;
}

.financial-stage-nav-btn.active {
  background: var(--btn-primary-color);
  color: #000000;
}

@keyframes spinFull {
  0% {
    transform: rotateY(0deg);
  }

  42.86% {
    transform: rotateY(360deg);
  }

  100% {
    transform: rotateY(360deg);
  }
}

.info-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1.4rem;
  padding-inline: 2rem;
}

#gallery-btn {
  display: none;
}

.round-components-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
}

.round-1-card-stack {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2.5rem 0;
  overflow: visible;
}

.round-1-card {
  position: relative;
  width: 300px;
  flex: 0 0 300px;
  transition: transform 180ms ease, filter 180ms ease;
  will-change: transform;
}

.round-1-card + .round-1-card {
  margin-left: -92px;
}

.round-1-card::before {
  content: '';
  position: absolute;
  inset: 18px 18px auto 18px;
  height: 76%;
  border-radius: 12px;
  opacity: 0.16;
  filter: blur(20px);
  z-index: 0;
  transition: opacity 180ms ease;
}

.stock-preview {
  z-index: 1;
  transform: translateY(12px);
  animation: stockFloat 5.4s ease-in-out infinite;
}

.equipment-preview {
  z-index: 2;
  transform: translateY(-6px);
  animation: equipmentFloat 4.8s ease-in-out infinite;
}

.product-preview {
  z-index: 3;
  transform: translateY(16px);
  animation: productFloat 6.1s ease-in-out infinite;
}

.stock-preview::before {
  background: rgba(67, 176, 249, 0.45);
}

.equipment-preview::before {
  background: rgba(95, 224, 179, 0.42);
}

.product-preview::before {
  background: rgba(245, 194, 107, 0.4);
}

.round-1-card:hover {
  z-index: 6;
  filter: saturate(1.03);
  animation-play-state: paused;
}

.stock-preview:hover {
  transform: translateY(-8px);
}

.equipment-preview:hover {
  transform: translateY(-12px);
}

.product-preview:hover {
  transform: translateY(-10px);
}

.round-1-card:hover::before {
  opacity: 0.28;
}

.round-1-card ::v-deep .md-card {
  width: 300px;
  margin: 0;
  position: relative;
  z-index: 1;
}

.round-1-card ::v-deep .stockBlock,
.round-1-card ::v-deep .equipmentBlock,
.round-1-card ::v-deep .productBlock {
  min-width: 300px;
  max-width: 300px;
}

.round-1-card ::v-deep .wrapper,
.round-1-card ::v-deep .md-layout {
  overflow: visible;
}

.product-preview ::v-deep iframe,
.product-preview ::v-deep svg {
  pointer-events: auto;
}

@keyframes stockFloat {
  0%, 100% {
    transform: translateY(12px);
  }
  50% {
    transform: translateY(0);
  }
}

@keyframes equipmentFloat {
  0%, 100% {
    transform: translateY(-6px);
  }
  50% {
    transform: translateY(-14px);
  }
}

@keyframes productFloat {
  0%, 100% {
    transform: translateY(16px);
  }
  50% {
    transform: translateY(6px);
  }
}

.step-desc {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.scoreboard-stage {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin: 0;
  padding: 0.25rem 0;
}

.scoreboard-stage-panel {
  width: 100%;
  display: flex;
  justify-content: center;
  overflow-x: auto;
}

.scoreboard-stage-view {
  width: 100%;
  display: flex;
  justify-content: center;
}

.scoreboard-stage-card {
  width: min(980px, 100%);
  margin: 0.5rem 0 0.25rem;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  background: var(--scoreboard-item-bg);
  box-shadow: 5px 5px 2px var(--box-shadow-color);
}

.scoreboard-stage-title {
  margin: 0 0 0.8rem;
  color: var(--text-primary);
  text-align: left;
  font-size: 1.25rem;
  letter-spacing: 0;
}

.scoreboard-products-card {
  width: min(1080px, 100%);
}

.scoreboard-products-card .product-graph-row {
  margin: 0 auto;
  max-width: 1000px;
}

.scoreboard-products-card .md-layout-item {
  min-width: 320px;
}

.scoreboard-products-card h3 {
  margin: 6px 0 10px;
}

.scoreboard-ratios-preview {
  width: 100%;
}

.scoreboard-ratios-preview ::v-deep .md-card.md-theme-default {
  border: 1px solid var(--border-color);
  box-shadow: none;
}

.statement-preview-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
  overflow-x: auto;
}

.statement-preview-wrap ::v-deep .income-statement-container {
  min-width: 680px;
}

.footer {
  width: calc(100% - 24px);
  margin-inline: 12px;
  height: 65px;
  background: var(--main-bg-color);
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.footer-text {
  position: absolute;
  bottom: 12px;
  margin: 0;
}

.footer-text:nth-child(1) {
  left: 12px;
}

.footer-text:nth-child(2) {
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.footer-text:nth-child(3) {
  right: 12px;
}

@media (max-width: 900px) {
  .footer {
    width: calc(100% - 16px);
    margin-inline: 8px;
  }

  .footer-text {
    bottom: 10px;
    font-size: 0.78rem;
  }

  .footer-text:nth-child(1) {
    left: 8px;
  }

  .footer-text:nth-child(3) {
    right: 8px;
  }
}

@media (max-width: 640px) {
  .button-link.secondary {
    display: none;
  }

  #gallery-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 150px;
  }

  .footer {
    height: auto;
    min-height: 56px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0;
  }

  .footer-text,
  .footer-text:nth-child(1),
  .footer-text:nth-child(2),
  .footer-text:nth-child(3) {
    position: static;
    left: auto;
    right: auto;
    bottom: auto;
    transform: none;
    text-align: center;
    display: block;
  }
}

.footer-email-selectable {
  user-select: text;
  -webkit-user-select: text;
}

@media (max-width: 1100px) {
  .container {
    min-height: unset;
    margin-top: 2px;
  }

  .info-container {
    flex-direction: column;
  }

  .round-components-container {
    display: none !important;
  }

  .round-1-card-stack {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .round-1-card + .round-1-card {
    margin-left: 0;
    margin-top: -48px;
  }

  .financial-stage {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  .scoreboard-stage-card {
    padding: 0.75rem;
  }
}
</style>
